import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { usePlatformEquipmentList } from '../../features/platformEquipment/api';
import { usePlatformExerciseList } from '../../features/platformExercises/api';
import { usePlatformBlockList } from '../../features/platformBlocks/api';
import { usePlatformTemplateList } from '../../features/platformTemplates/api';

/**
 * SuperAdmin Dashboard component showing platform management overview
 */
export function SuperAdminDashboard() {
  // Fetch counts from APIs using the actual list endpoints with high page size to get totals
  const { data: equipmentData } = usePlatformEquipmentList({ pageSize: 1000, includeInactive: false });
  const { data: exerciseData } = usePlatformExerciseList({ pageSize: 1000, includeInactive: false });
  const { data: blockData } = usePlatformBlockList({ pageSize: 1000, includeInactive: false });
  const { data: templateData } = usePlatformTemplateList({ pageSize: 1000, includeInactive: false });

  const statsCards = [
    {
      title: 'Platform Equipment',
      description: 'Global equipment library',
      value: equipmentData?.pagination?.totalCount?.toString() || '0',
      action: 'Manage Equipment',
      href: '/superadmin/equipment',
      icon: '🏋️',
    },
    {
      title: 'Platform Exercises',
      description: 'Global exercise database',
      value: exerciseData?.pagination?.totalCount?.toString() || '0',
      action: 'Manage Exercises',
      href: '/superadmin/exercises',
      icon: '🤸',
    },
    {
      title: 'Platform Blocks',
      description: 'Reusable session blocks',
      value: blockData?.pagination?.totalCount?.toString() || '0',
      action: 'Manage Blocks',
      href: '/superadmin/blocks',
      icon: '🧩',
    },
    {
      title: 'Platform Templates',
      description: 'Session templates',
      value: templateData?.pagination?.totalCount?.toString() || '0',
      action: 'Manage Templates',
      href: '/superadmin/templates',
      icon: '📋',
    },
  ];
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Platform Management Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Manage global platform content that will be available to all studios
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat) => (
          <Card key={stat.title} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-600">
                  {stat.title}
                </CardTitle>
                <span className="text-2xl">{stat.icon}</span>
              </div>
              <CardDescription className="text-xs">
                {stat.description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 mb-2">
                {stat.value}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.href = stat.href}
                className="w-full"
              >
                {stat.action}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Platform Updates</CardTitle>
          <CardDescription>
            Latest changes to platform content
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-md">
              <span className="text-blue-600">ℹ️</span>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Platform Equipment Library Ready
                </p>
                <p className="text-xs text-gray-600">
                  Global equipment management is now available for SuperAdmins
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-md">
              <span className="text-green-600">✅</span>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Platform Exercise Library Active
                </p>
                <p className="text-xs text-gray-600">
                  Exercise library with validation is ready for content management
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-3 bg-purple-50 rounded-md">
              <span className="text-purple-600">🚀</span>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Platform Templates Now Available
                </p>
                <p className="text-xs text-gray-600">
                  Global session template management system is operational
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common platform management tasks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="outline"
              className="h-20 flex-col space-y-2"
              onClick={() => window.location.href = '/superadmin/equipment/new'}
            >
              <span className="text-2xl">🏋️</span>
              <span>Add Equipment</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex-col space-y-2"
              onClick={() => window.location.href = '/superadmin/exercises/new'}
            >
              <span className="text-2xl">🤸</span>
              <span>Create Exercise</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex-col space-y-2"
              onClick={() => window.location.href = '/superadmin/templates/new'}
            >
              <span className="text-2xl">📋</span>
              <span>New Template</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}